/* eslint-disable react/require-default-props */
import * as React from 'react';
import { Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import SearchIcon from '@mui/icons-material/Search';
import { useMediaQuery, useTheme } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import { LoadedUser } from '../../interfaces';
import DropdownNavMenu from './DropdownNavMenu';
import FreeTrialBanner from '../subscriptions/FreeTrialBanner';
import useFreeTrialStatus from '../../hooks/subscriptions/useFreeTrialStatus';
import useUser from '../../hooks/global/useUser';
import useSearchModal from '../../hooks/global/useSearchModal';
import './navigation.css';
import { AppContext } from '../../context/AppContext/context';

const URL = {
  chat: '/chat',
  support: 'https://support.getporter.io',
};

type Props = {
  mobileDrawerOpen: boolean;
  setMobileDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  loadedUser?: LoadedUser;
};

function ResponsiveAppBar({
  mobileDrawerOpen,
  setMobileDrawerOpen,
  loadedUser,
}: Props) {
  const { user } = useUser();
  const theme = useTheme();
  const matches = useMediaQuery('(min-width:900px)');
  const {
    state: { unreadConversations },
  } = React.useContext(AppContext);
  const [artistName, setArtistName] = React.useState<string>();
  const [artistInitials, setArtistInitials] = React.useState<string>();

  const { open } = useSearchModal();
  const { isBannerShown } = useFreeTrialStatus();

  const pages = React.useMemo(
    () => [
      {
        onClick: open,
        icon: SearchIcon,
      },
      {
        url: URL.chat,
        icon: '/messenger.svg',
      },
      {
        label: 'Support',
        url: URL.support,
        icon: '/Support.svg',
      },
    ],
    [open],
  );

  const unreadMessages = user
    ? Object.values(unreadConversations)?.reduce(
        (total, currentCount) => total + currentCount,
        0,
      ) || 0
    : 0;

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleDrawerChange = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  React.useEffect(() => {
    setArtistName(`${loadedUser?.firstName} ${loadedUser?.lastName}`);
    setArtistInitials(
      `${loadedUser?.firstName.charAt(0)}${loadedUser?.lastName.charAt(0)}`,
    );
  }, [loadedUser?.firstName, loadedUser?.lastName]);

  const classes = React.useMemo(() => {
    const list: string[] = [];
    list.push('primary-nav');
    if (isBannerShown) list.push('banner-above');
    return list;
  }, [isBannerShown]);

  const renderIcon = (icon: string | SvgIconComponent) => {
    if (typeof icon === 'string') {
      return <img src={icon} alt="icon" width="18px" />;
    }
    const Icon = icon;
    return (
      <Icon
        sx={{
          color: '#333333',
          width: '18px',
        }}
      />
    );
  };

  return (
    <AppBar
      position="fixed"
      className={classes.join(' ')}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
      }}>
      <FreeTrialBanner />
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            height: '64px',
          }}>
          <img src="/Logo.svg" alt="Porter logo" className="desktop-logo" />
          <div className="mobile-dropdown">
            <IconButton
              style={{ color: '#333333' }}
              aria-label="open drawer"
              onClick={handleDrawerChange}
              edge="start">
              <MenuIcon />
            </IconButton>
            <img src="/Logo.svg" alt="Porter logo" />
          </div>
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'end' }}>
            {pages.map((page, i) => (
              <Button
                key={page.label || i}
                to={page.url}
                component={page.url ? Link : Button}
                onClick={() => {
                  setMobileDrawerOpen(false);
                  page.onClick?.();
                }}
                target={page.url?.startsWith('/') ? '_self' : '_blank'}
                sx={{
                  borderRight: '1px solid black',
                  marginRight: 1,
                  border: `1px solid ${theme.palette.outlineGrey.main}`,
                  borderRadius: '5px',
                  minWidth: 0,
                  my: 2,
                  display: {
                    xs: page.url === URL.support ? 'none' : 'flex',
                    md: 'flex',
                  },
                  gap: 1,
                }}>
                {page.url === '/chat' && !!unreadMessages ? (
                  <Badge color="secondary" badgeContent={unreadMessages}>
                    {renderIcon(page.icon)}
                  </Badge>
                ) : (
                  renderIcon(page.icon)
                )}
                {page.label && (
                  <Typography variant="h5">{page.label}</Typography>
                )}
              </Button>
            ))}
          </Box>
          {loadedUser && (
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar
                    sx={{
                      bgcolor: '#333333',
                      width: 30,
                      height: 30,
                      fontSize: 14,
                      marginRight: matches ? 0 : 1,
                    }}>
                    {artistInitials || '  '}
                  </Avatar>
                  <div className="avatar-text hidden-mobile">
                    <Typography variant="h5">{artistName}</Typography>
                    <Typography variant="body2">
                      {loadedUser?.business.name || ''}
                    </Typography>
                  </div>
                  <img src="/chevron-down.svg" alt="dropdown" width="14px" />
                </IconButton>
              </Tooltip>
              <DropdownNavMenu
                anchorElUser={anchorElUser}
                handleCloseDropdown={handleCloseUserMenu}
                setMobileDrawerOpen={setMobileDrawerOpen}
              />
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
}
export default ResponsiveAppBar;
